import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, User, Mail, Truck, Building2, Store, FileText } from 'lucide-react';
import { HelpIcon } from '../components/HelpIcon';
import { apiClient } from '../utils/api';
import { useShopScopedPage } from '../hooks/useShopScopedReload';
import { ShopSelectionGate } from '../components/ShopSelectionGate';
import { useToast } from '../components/Toast';
import { InternationalPhoneInput } from '../components/InternationalPhoneInput';
import { type SupportedPhoneCountry } from '@digitaltattoo/shared-utils';

interface VendorFormData {
  firstName: string;
  lastName: string;
  code: string;
  email: string;
  phone: string;
  companyId?: string;
  shopId?: string;
  notes: string;
  isActive: boolean;
}

export function VendorCreatePage() {
  const navigate = useNavigate();
  const { showToast, ToastComponent } = useToast();
  const { needsShopSelection, canLoad, currentShopId } = useShopScopedPage({ requireSingleShop: true });

  const [formData, setFormData] = useState<VendorFormData>({
    firstName: '',
    lastName: '',
    code: '',
    email: '',
    phone: '',
    companyId: '',
    shopId: '',
    notes: '',
    isActive: true,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<string[]>([]);
  const [companies, setCompanies] = useState<Array<{ id: string; name: string }>>([]);
  const [shops, setShops] = useState<Array<{ id: string; name: string }>>([]);
  const [phoneCountry, setPhoneCountry] = useState<SupportedPhoneCountry>('FR');
  const [phoneDisplay, setPhoneDisplay] = useState('');
  const [phoneE164, setPhoneE164] = useState<string | null>(null);
  const [phoneIsValid, setPhoneIsValid] = useState(false);

  useEffect(() => {
    if (!canLoad) return;
    if (currentShopId) {
      setFormData((prev) => ({ ...prev, shopId: currentShopId }));
    }
    loadCompanies();
    loadShops();
  }, [canLoad, currentShopId]);

  const loadCompanies = async () => {
    try {
      const response = await apiClient('/api/admin/companies?limit=200');

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setCompanies((data.data?.companies || []).map((c: any) => ({ id: c.id, name: c.name })));
        }
      }
    } catch (err) {
      console.error('Failed to load companies:', err);
    }
  };

  const loadShops = async () => {
    try {
      const response = await apiClient('/api/admin/shops?limit=200');

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setShops((data.data?.shops || []).map((s: any) => ({ id: s.id, name: s.name })));
        }
      }
    } catch (err) {
      console.error('Failed to load shops:', err);
    }
  };

  const handleInputChange = (field: keyof VendorFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.firstName || !formData.lastName || !formData.email || !phoneDisplay) {
      setError('Veuillez remplir tous les champs obligatoires.');
      return;
    }
    if (!/^[A-Z0-9]{4}$/.test(formData.code.trim().toUpperCase())) {
      setError('Le code (4 caractères alphanumériques) est obligatoire.');
      return;
    }
    if (!phoneIsValid || !phoneE164) {
      setError('Le numéro de téléphone est invalide pour le pays sélectionné.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setErrorDetails([]);
      const response = await apiClient('/api/admin/vendors', {
        method: 'POST',
        body: JSON.stringify({
          ...formData,
          code: formData.code.trim().toUpperCase(),
          phone: phoneE164,
          phoneCountry,
          companyId: formData.companyId || null,
          shopId: formData.shopId || currentShopId || null,
        }),
      });

      const data = await response.json().catch(() => null);
      if (!response.ok || !data?.success) {
        const reason = data?.message || 'Erreur lors de la création du vendeur';
        setError(reason);
        setErrorDetails(Array.isArray(data?.errors) ? data.errors : []);
        showToast(reason, 'error', 7000);
        return;
      }

      showToast('Vendeur créé avec succès', 'success');
      navigate('/vendors');
    } catch (err) {
      console.error('Create vendor error:', err);
      setError('Erreur de connexion au serveur');
      setErrorDetails([]);
      showToast('Erreur de connexion au serveur', 'error', 7000);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/vendors');
  };

  if (needsShopSelection) {
    return <ShopSelectionGate requireSingleShop />;
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <button
            onClick={handleCancel}
            className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors border border-gray-300 shadow-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Retour à la liste</span>
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Nouveau vendeur</h1>
            <p className="text-sm text-gray-500">Créer un profil vendeur et l’associer à un shop ou une entreprise</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <HelpIcon docPath="vendors" />
          <button
            type="submit"
            form="vendor-form"
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {loading ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </div>

      <ToastComponent />

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800 font-medium">{error}</p>
          {errorDetails.length > 0 && (
            <ul className="mt-2 text-sm text-red-700 list-disc list-inside space-y-1">
              {errorDetails.map((detail, i) => (
                <li key={i}>{detail}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      <form id="vendor-form" onSubmit={handleSubmit} autoComplete="off" className="space-y-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-6">
            <User className="w-5 h-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900">Informations personnelles</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Prénom *</label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                placeholder="Jean"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Nom *</label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                placeholder="Dupont"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Code *</label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => handleInputChange('code', e.target.value.toUpperCase())}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 uppercase"
                placeholder="JD01"
                maxLength={4}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                  placeholder="contact@vendeur.fr"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone *</label>
              <InternationalPhoneInput
                value={phoneDisplay}
                country={phoneCountry}
                onChange={(result) => {
                  setPhoneDisplay(result.displayValue);
                  setPhoneCountry(result.country);
                  setPhoneE164(result.e164Value);
                  setPhoneIsValid(result.isValid);
                  handleInputChange('phone', result.displayValue);
                  if (result.isValid) {
                    setError((prev) =>
                      prev?.startsWith('Le numéro de téléphone est invalide') ? null : prev,
                    );
                  }
                }}
                selectClassName="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                inputClassName="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                placeholder="+33 6 12 34 56 78"
                required
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-6">
            <Truck className="w-5 h-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900">Association</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Entreprise</label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <select
                  value={formData.companyId}
                  onChange={(e) => handleInputChange('companyId', e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 appearance-none"
                >
                  <option value="">Aucune</option>
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Shop</label>
              <div className="relative">
                <Store className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <select
                  value={formData.shopId}
                  onChange={(e) => handleInputChange('shopId', e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 appearance-none"
                >
                  <option value="">Aucun</option>
                  {shops.map((shop) => (
                    <option key={shop.id} value={shop.id}>
                      {shop.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-6">
            <FileText className="w-5 h-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900">Notes & statut</h2>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                placeholder="Informations supplémentaires sur le vendeur"
              />
            </div>
            <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => handleInputChange('isActive', e.target.checked)}
                className="h-4 w-4 text-primary-600 border-gray-300 rounded"
              />
              Vendeur actif
            </label>
          </div>
        </div>
      </form>
    </div>
  );
}
